// ---- no itx for this file----
// Translation table for KNX channel and device terms.
// Each entry contains the same concept in all supported languages
// (de, en, fr, es, it). The table is used by translateChannelName() to
// automatically translate German manufacturer names into the user's language.
const ScriptTranslations = [
  { de: "Einzeltaster", en: "Single button", fr: "Bouton simple", es: "Botón simple", it: "Pulsante singolo" },
  { de: "Taster", en: "Button", fr: "Bouton", es: "Botón", it: "Pulsante" },
  {
    de: "Wippe",
    en: "Rocker",
    fr: "Bascule",
    es: "Balancín",
    it: "Interruttore a bilanciere",
  },
  {
    de: "Kanal",
    en: "Channel",
    fr: "Canal",
    es: "Canal",
    it: "Canale",
  },
  {
    de: "Aktor",
    en: "Actor",
    fr: "Actionneur",
    es: "Actuador",
    it: "Attuatore",
  },
  {
    de: "Hilfsschalter",
    en: "Auxiliary",
    fr: "Auxiliaire",
    es: "Auxiliar",
    it: "Ausiliario",
  },
  {
    de: "Funktion",
    en: "Function",
    fr: "Fonction",
    es: "Función",
    it: "Funzione",
  },
  { de: "Aktuator", en: "Actuator", fr: "Actionneur", es: "Actuador", it: "Attuatore" },
  {
    de: "Temperatur-Messung",
    en: "Temperature measurement",
    fr: "Mesure de température",
    es: "Medición de temperatura",
    it: "Misura della temperatura",
  },
  { de: "Temperatur", en: "Temperature", fr: "Température", es: "Temperatura", it: "Temperatura" },

  { de: "Proximity", en: "Proximity", fr: "Proximité", es: "Proximidad", it: "Prossimità" },

  { de: "Bildschirm", en: "Screen", fr: "Écran", es: "Pantalla", it: "Schermo" },
  {
    de: "Unbenutzte Signale",
    en: "Unused signals",
    fr: "Signaux inutilisés",
    es: "Señales no utilizadas",
    it: "Segnali inutilizzati",
  },
  {
    de: "Spezial Signale",
    en: "Special signals",
    fr: "Signaux spéciaux",
    es: "Señales especiales",
    it: "Segnali speciali",
  },
  { de: "Status", en: "Status", fr: "Statut", es: "Estado", it: "Stato" },
  { de: "Logik", en: "Logic", fr: "Logique", es: "Lógica", it: "Logica" },
  { de: "Patsch", en: "Patch", fr: "Patch", es: "Parche", it: "Patsch" },
  { de: "Kontakt", en: "Contact", fr: "Contact", es: "Contacto", it: "Contatto" },
  { de: "Umgebung", en: "Environment", fr: "Environnement", es: "Entorno", it: "Ambiente" },
  { de: "Licht", en: "Light", fr: "Lumière", es: "Luz", it: "Luce" },
  { de: "Ausgang", en: "Output", fr: "Sortie", es: "Salida", it: "Uscita" },
  { de: "Eingang", en: "Input", fr: "Entrée", es: "Entrada", it: "Ingresso" },
];

/**
 * Translates a channel name from the KNX product database into the current
 * user language (from `state.settings.language`).
 *
 * The function searches the given `channelName` for known terms from
 * `ScriptTranslations` (case-insensitive) and replaces each match with the
 * corresponding translation in the target language. Unrecognised parts remain
 * unchanged.
 *
 * @param channelName - The original channel name as it comes from the ETS / product database.
 * @returns The translated channel name, or the original name if no entry matches.
 *
 * @example
 * // User language = "en"
 * translateChannelName("Taster Kanal 1")  // → "Button Channel 1"
 */
export default function translateChannelName(channelName: string): string {
  // Determine the user language (two-letter ISO code, e.g. "de", "en", "fr")
  let userLanguageEnum = state.settings.language.toLocaleLowerCase().substring(0, 2);
  let translatedChannelName = channelName;

  // Iterate over every entry in the translation table
  ScriptTranslations.forEach((translations) => {
    // Check all language variants of the entry (de, en, fr, ...)
    for (const key in translations) {
      const textToFind = translations[key as keyof typeof translations].toLocaleLowerCase();
      // Find the position of the term in the (already partially translated) channel name
      let placement = translatedChannelName.toLocaleLowerCase().indexOf(textToFind);

      if (placement !== -1) {
        // Retrieve the matching translation for the target language
        const translatedText = translations[userLanguageEnum as keyof typeof translations];
        // check if before translation test is a space or sonderzeichen or beginning of the string
        const beforeChar = placement > 0 ? translatedChannelName[placement - 1] : " ";

        if (translatedText && beforeChar.match(/[\s\W]|^/)) {
          // Replace the found term with the target-language variant
          // only if it is not the same language
          if (key !== userLanguageEnum) {
            translatedChannelName =
              translatedChannelName.substring(0, placement) +
              translatedText +
              translatedChannelName.substring(placement + textToFind.length);
          }
        }
        break; // Stop checking other language variants for this entry, move to the next entry in the table
      }
    }
  });

  return translatedChannelName;
}
